import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown, ArrowUp, Crown, Database, Minus, Trophy } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { averageAnswerMs, formatDuration, teamPerformanceLabel } from '../lib/team-performance';
import type { ArenaEvent, TeamPublic } from '../types/arena';

export function LeaderboardPage({ event, teams }: { event: ArenaEvent; teams: TeamPublic[] }) {
  const previousRanks = useRef(new Map<string, number>());
  const currentRanks = new Map(teams.map((team, index) => [team.id, index + 1]));

  useEffect(() => {
    const timeout = window.setTimeout(() => { previousRanks.current = currentRanks; }, 900);
    return () => window.clearTimeout(timeout);
  });

  if (event.status === 'finished') return <FinalLeaderboard teams={teams} />;

  return (
    <main className="min-h-screen bg-[#F6F8FC] text-ink">
      <div className="grid min-h-screen lg:grid-cols-[1fr_280px]">
        <section className="px-8 py-8">
          <header className="mb-10 flex flex-wrap items-center justify-between gap-5">
            <div>
              <span className="text-xs font-black text-brand">مسابقه دیتابینکس T-SQL</span>
              <h1 className="mt-2 text-3xl font-black">جدول رتبه‌بندی زنده</h1>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-2 text-emerald-600"><span className="size-3 rounded-full bg-emerald-500" /> زنده</span>
              <span>آخرین به‌روزرسانی خودکار</span>
              <span className="rounded-xl border border-slate-200 bg-white px-3 py-2">هر ۱ ثانیه</span>
            </div>
          </header>
          <div className="mb-5 grid gap-3 md:grid-cols-4">
            <LiveMetric label="تیم‌ها" value={teams.length.toLocaleString('fa-IR')} />
            <LiveMetric label="کل Query کامل" value={teams.reduce((sum, team) => sum + team.correctCount, 0).toLocaleString('fa-IR')} />
            <LiveMetric label="رهبر فعلی" value={teams[0]?.name ?? '—'} />
            <LiveMetric label="وضعیت" value="روی خط" />
          </div>
          <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm">
            <div className="grid min-w-[980px] grid-cols-[64px_1.25fr_120px_105px_135px_160px_1fr_76px] items-center border-b border-slate-200 bg-slate-50 px-5 py-4 text-[10px] font-bold text-slate-400"><span>رتبه</span><span>تیم</span><span>امتیاز کل</span><span>حل‌شده</span><span>میانگین سرعت</span><span>برچسب تیم</span><span>پیشرفت</span><span>تغییر</span></div>
            <div className="overflow-x-auto">
        <motion.div layout className="space-y-2.5">
          <AnimatePresence initial={false}>
            {teams.map((team, index) => {
              const rank = index + 1;
              const oldRank = previousRanks.current.get(team.id) ?? rank;
              const delta = oldRank - rank;
              return (
                <motion.div layout key={team.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ layout: { type: 'spring', stiffness: 360, damping: 32 } }} className={`relative grid min-h-[76px] min-w-[980px] grid-cols-[64px_1.25fr_120px_105px_135px_160px_1fr_76px] items-center border-b border-slate-100 px-5 last:border-0 ${rank === 1 ? 'bg-amber-50/70 ring-1 ring-inset ring-amber-300' : 'bg-white'}`}>
                  <div className="relative flex items-center gap-2">{rank === 1 ? <Crown className="size-6 fill-amber-400 text-amber-400" /> : <strong className="font-mono text-xl text-slate-500">{rank.toLocaleString('fa-IR')}</strong>}</div>
                  <div className="relative flex min-w-0 items-center gap-4"><TeamAvatar avatarId={team.avatarId} color={team.color} className="size-12" /><div className="min-w-0"><b className="block truncate text-base">{team.name}</b><span className="block truncate text-[10px] text-slate-400">{team.slogan || team.members.join('، ')}</span></div></div>
                  <motion.strong key={team.score} initial={{ scale: 1.22, color: '#2563FF' }} animate={{ scale: 1, color: '#071225' }} className="font-mono text-2xl">{team.score.toLocaleString('fa-IR')}</motion.strong>
                  <span className="flex items-center gap-2 font-mono text-base"><Trophy className="size-4 text-emerald-400" />{team.correctCount.toLocaleString('fa-IR')}</span>
                  <span className="font-mono text-xs text-slate-500">{formatDuration(averageAnswerMs(team))}</span>
                  <span className="w-fit rounded-full border border-brand/25 bg-brand/10 px-3 py-1 text-[10px] font-black text-brand">{teamPerformanceLabel(team)}</span>
                  <div className="pl-8"><div className="mb-2 flex items-center justify-between text-[9px] text-slate-400"><span>{team.progress.toLocaleString('fa-IR')} از {TEAM_QUESTION_COUNT.toLocaleString('fa-IR')}</span><span>{Math.round((team.progress / TEAM_QUESTION_COUNT) * 100).toLocaleString('fa-IR')}٪</span></div><div className="h-1.5 overflow-hidden rounded-full bg-slate-100"><motion.div className="h-full rounded-full" style={{ backgroundColor: team.color }} animate={{ width: `${Math.min(100, (team.progress / TEAM_QUESTION_COUNT) * 100)}%` }} /></div></div>
                  <span className={`flex items-center gap-1 font-mono text-xs ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-slate-300'}`}>{delta > 0 ? <ArrowUp className="size-4" /> : delta < 0 ? <ArrowDown className="size-4" /> : <Minus className="size-4" />}{delta ? Math.abs(delta).toLocaleString('fa-IR') : '—'}</span>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
            </div>
          </div>
        {!teams.length && <div className="grid h-[55vh] place-items-center text-center text-slate-400"><div><Database className="mx-auto mb-4 size-9" />هنوز تیمی وارد میدان نشده است.</div></div>}
        </section>
        <aside className="hidden bg-[#071225] p-6 text-white lg:block">
          <BrandLockup inverse />
          <div className="mt-10 rounded-3xl border border-white/10 bg-white/[.055] p-5">
            <span className="text-xs text-white/45">مدیر مسابقه</span>
            <b className="mt-1 block">DataBinex Arena</b>
          </div>
          <nav className="mt-8 space-y-2 text-sm text-white/55">
            <div className="rounded-2xl bg-brand px-4 py-3 font-black text-white">جدول رتبه‌بندی زنده</div>
            <div className="px-4 py-3">سؤالات</div>
            <div className="px-4 py-3">تحلیل‌ها</div>
            <div className="px-4 py-3">گزارش‌ها</div>
          </nav>
          <div className="mt-auto pt-24 text-xs leading-6 text-white/35">قدرت داده، مهارت تحلیل<br />نسخه ۱.۰</div>
        </aside>
      </div>
    </main>
  );
}

function LiveMetric({ label, value }: { label: string; value: string }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><span className="text-[10px] font-bold text-slate-400">{label}</span><b className="mt-2 block truncate text-xl font-black">{value}</b></div>;
}

function FinalLeaderboard({ teams }: { teams: TeamPublic[] }) {
  const [winner, second, third] = teams;
  return (
    <main className="dbx-aurora arena-grid min-h-screen overflow-hidden px-8 py-7 text-white">
      <header className="mx-auto flex max-w-7xl items-center justify-between"><BrandLockup inverse /><span className="rounded-full border border-white/10 bg-white/[.06] px-4 py-2 text-xs text-white/55">نتایج نهایی</span></header>
      <section className="mx-auto mt-8 max-w-7xl text-center">
        <Trophy className="mx-auto size-20 fill-gold/25 text-gold" />
        <h1 className="mt-4 text-5xl font-black">نتایج نهایی مسابقه دیتابینکس T-SQL</h1>
        <p className="mt-4 text-lg text-white/55">تبریک به تمام شرکت‌کنندگان برای عملکرد درخشان.</p>
        <div className="mt-10 grid items-end gap-6 lg:grid-cols-3">
          <PodiumCard team={second} rank={2} />
          <PodiumCard team={winner} rank={1} featured />
          <PodiumCard team={third} rank={3} />
        </div>
        <div className="mt-8 overflow-hidden rounded-[26px] border border-white/10 bg-white/[.075] text-right backdrop-blur-xl">
          <div className="grid grid-cols-[80px_1fr_150px_150px_150px] border-b border-white/10 px-5 py-4 text-[10px] font-bold text-white/40"><span>رتبه</span><span>تیم</span><span>امتیاز کل</span><span>درصد صحت</span><span>زمان کل</span></div>
          {teams.slice(3, 10).map((team, index) => (
            <div key={team.id} className="grid grid-cols-[80px_1fr_150px_150px_150px] items-center border-b border-white/8 px-5 py-4 last:border-0">
              <b>{(index + 4).toLocaleString('fa-IR')}</b>
              <span className="flex items-center gap-3"><TeamAvatar avatarId={team.avatarId} color={team.color} className="size-9 rounded-xl" />{team.name}</span>
              <b className="font-mono">{team.score.toLocaleString('fa-IR')}</b>
              <span>{Math.round((team.correctCount / Math.max(1, team.submittedCount)) * 100).toLocaleString('fa-IR')}٪</span>
              <span>{formatDuration(averageAnswerMs(team))}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function PodiumCard({ team, rank, featured = false }: { team: TeamPublic | undefined; rank: number; featured?: boolean }) {
  if (!team) return <div />;
  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className={`relative rounded-[28px] border p-7 ${featured ? 'border-gold/60 bg-white/[.12] shadow-[0_0_80px_rgba(248,185,49,.16)] lg:pb-12' : 'border-white/15 bg-white/[.07]'}`}>
      <span className={`absolute -top-5 left-1/2 grid size-11 -translate-x-1/2 place-items-center rounded-full font-black ${rank === 1 ? 'bg-gold text-ink' : 'bg-white text-slate-600'}`}>{rank.toLocaleString('fa-IR')}</span>
      <TeamAvatar avatarId={team.avatarId} color={team.color} className="mx-auto mt-4 size-20 rounded-[24px] text-2xl" />
      <h2 className="mt-5 text-2xl font-black">{team.name}</h2>
      <p className="mt-2 text-sm text-white/45">{team.score.toLocaleString('fa-IR')} امتیاز</p>
      <p className="mt-2 font-mono text-white/65">{formatDuration(averageAnswerMs(team))}</p>
    </motion.div>
  );
}
